// Doc/URL blip client — the second BriefBlip rung.
//
// The endpoint streams its progress so the UI can show the agent working rather
// than a spinner. EventSource cannot issue a POST and cannot set headers, so the
// stream is read straight off the fetch body with a ReadableStream reader and the
// SSE framing is parsed here.
//
// Wire contract — api/briefblip/blip.php, verified against that file:
//   POST {API_BASE}/api/briefblip/blip.php
//     body: { "url": "https://..." }  OR  { "text": "...", "title": "<optional>" }
//   ->  text/event-stream, `data: <json>` per event:
//         {"stage":"validating"}
//         {"stage":"fetching","domain":"example.com"}
//         {"stage":"extracting","chars":12345}
//         {"stage":"composing"}
//       then exactly one terminal event:
//         {"done":true,"slug","url","kind","brief":{...},"cost":{...}}
//       or, at any point:
//         {"error":"<message>"}
//
// 🔴 The endpoint commits to HTTP 200 the moment it flushes its first byte, so a
// failure after that point arrives as a terminal {"error"} event on an otherwise
// successful response. Checking res.ok is therefore necessary but NOT sufficient —
// the error event below is the other half, and dropping it would render a total
// failure as a hung timeline.

import { API_BASE, BriefblipError } from './briefblip';

export const BLIP_STAGES = ['validating', 'fetching', 'extracting', 'composing'] as const;

export type BlipStage = (typeof BLIP_STAGES)[number];

export const STAGE_LABELS: Record<BlipStage, { label: string; detail: string }> = {
  validating: { label: 'Validating', detail: 'Checking the source is reachable and safe to read' },
  fetching: { label: 'Fetching', detail: 'Pulling the document down' },
  extracting: { label: 'Extracting', detail: 'Stripping chrome, keeping the prose' },
  composing: { label: 'Composing', detail: 'Writing the brief and pinning each quote' },
};

/** A stage event, plus whatever detail that particular stage reports. */
export interface StageEvent {
  stage: BlipStage;
  /** `fetching` only — the host the document came from. */
  domain?: string;
  /** `extracting` only — characters of prose recovered. */
  chars?: number;
}

export interface BlipKeyPoint {
  point: string;
  /**
   * A span the backend confirmed appears verbatim in the source. It is null when
   * the model either offered no quote or offered one that failed that check —
   * the backend drops unverified quotes rather than passing them through.
   */
  quote: string | null;
  quote_verified: boolean;
}

export interface BlipBrief {
  title: string;
  tl_dr: string;
  key_points: BlipKeyPoint[];
  facts_figures: string[];
  who_should_care: string;
  reading_time_min: number;
  /** How many quotes the model invented on this document and had rejected. */
  quotes_rejected: number;
}

export interface BlipCost {
  usd: number;
  input_tokens: number;
  output_tokens: number;
  model: string;
}

export interface BlipResult {
  slug: string;
  /** Canonical permalink, minted by the backend — always prefer it over building one. */
  url: string;
  kind: string;
  brief: BlipBrief;
  cost: BlipCost | null;
}

export interface BlipInput {
  url?: string;
  text?: string;
  title?: string;
}

export interface StreamBlipOptions {
  onStage?: (event: StageEvent) => void;
  signal?: AbortSignal;
}

function isStage(value: unknown): value is BlipStage {
  return typeof value === 'string' && (BLIP_STAGES as readonly string[]).includes(value);
}

function str(value: unknown): string {
  return typeof value === 'string' ? value.trim() : '';
}

function num(value: unknown): number {
  return typeof value === 'number' && Number.isFinite(value) ? value : 0;
}

function strList(value: unknown): string[] {
  if (!Array.isArray(value)) return [];
  return value.map((v) => str(v)).filter((v) => v !== '');
}

function normaliseKeyPoints(raw: unknown): BlipKeyPoint[] {
  if (!Array.isArray(raw)) return [];
  const out: BlipKeyPoint[] = [];
  for (const item of raw) {
    if (typeof item === 'string') {
      const point = item.trim();
      if (point) out.push({ point, quote: null, quote_verified: false });
      continue;
    }
    if (item && typeof item === 'object') {
      const rec = item as Record<string, unknown>;
      const point = str(rec.point);
      if (!point) continue;
      const quote = str(rec.quote);
      out.push({
        point,
        quote: quote !== '' ? quote : null,
        quote_verified: rec.quote_verified === true,
      });
    }
  }
  return out;
}

function normaliseBrief(raw: unknown): BlipBrief {
  const rec = (raw && typeof raw === 'object' ? raw : {}) as Record<string, unknown>;
  return {
    title: str(rec.title) || 'Untitled document',
    tl_dr: str(rec.tl_dr),
    key_points: normaliseKeyPoints(rec.key_points),
    facts_figures: strList(rec.facts_figures),
    who_should_care: str(rec.who_should_care),
    reading_time_min: num(rec.reading_time_min),
    quotes_rejected: num(rec.quotes_rejected),
  };
}

function normaliseCost(raw: unknown): BlipCost | null {
  if (!raw || typeof raw !== 'object') return null;
  const rec = raw as Record<string, unknown>;
  const model = str(rec.model);
  if (!model) return null;
  return {
    usd: num(rec.usd),
    input_tokens: num(rec.input_tokens),
    output_tokens: num(rec.output_tokens),
    model,
  };
}

/**
 * Pull the `data:` payload out of one SSE frame.
 *
 * Returns null for frames that carry no payload (comment heartbeats, bare
 * `event:` lines). Falls back to treating the whole frame as the payload so a
 * backend that emits plain NDJSON instead of SSE framing still works.
 */
function framePayload(frame: string): string | null {
  const lines = frame.split(/\r?\n/);
  const data: string[] = [];
  let sawField = false;

  for (const line of lines) {
    if (line === '' || line.startsWith(':')) continue;
    const colon = line.indexOf(':');
    const field = colon === -1 ? line : line.slice(0, colon);
    if (field === 'data') {
      sawField = true;
      const value = colon === -1 ? '' : line.slice(colon + 1);
      data.push(value.startsWith(' ') ? value.slice(1) : value);
    } else if (field === 'event' || field === 'id' || field === 'retry') {
      sawField = true;
    }
  }

  if (data.length > 0) return data.join('\n');
  if (sawField) return null;

  const bare = frame.trim();
  return bare.startsWith('{') ? bare : null;
}

async function errorFromResponse(res: Response): Promise<BriefblipError> {
  let message = `Request failed (HTTP ${res.status})`;
  try {
    const ct = res.headers.get('content-type') ?? '';
    if (ct.includes('application/json')) {
      const body = (await res.json()) as { error?: unknown };
      if (body && typeof body.error === 'string' && body.error.trim()) message = body.error.trim();
    } else {
      const text = (await res.text()).trim();
      if (text && text.length < 300) message = text;
    }
  } catch {
    // Body unreadable — the status-code message above still tells the user something.
  }
  return new BriefblipError(res.status, message);
}

export async function streamBlip(
  input: BlipInput,
  options: StreamBlipOptions = {},
): Promise<BlipResult> {
  const { onStage, signal } = options;

  const res = await fetch(`${API_BASE}/api/briefblip/blip.php`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json', Accept: 'text/event-stream' },
    body: JSON.stringify(input),
    signal,
  });

  if (!res.ok) throw await errorFromResponse(res);
  if (!res.body) throw new BriefblipError(0, 'Streaming is not supported by this browser.');

  const reader = res.body.getReader();
  const decoder = new TextDecoder();
  let buffer = '';
  let result: BlipResult | null = null;

  const handleFrame = (frame: string) => {
    const payload = framePayload(frame);
    if (payload === null || payload === '' || payload === '[DONE]') return;

    let event: Record<string, unknown>;
    try {
      event = JSON.parse(payload) as Record<string, unknown>;
    } catch {
      return; // Partial or non-JSON keepalive — nothing actionable.
    }

    if (typeof event.error === 'string' && event.error.trim()) {
      // Terminal failure on an already-200 response. See the header note.
      throw new BriefblipError(res.status, event.error.trim());
    }
    if (event.done === true) {
      result = {
        slug: str(event.slug),
        url: str(event.url),
        kind: str(event.kind),
        brief: normaliseBrief(event.brief),
        cost: normaliseCost(event.cost),
      };
      return;
    }
    if (isStage(event.stage)) {
      const detail: StageEvent = { stage: event.stage };
      const domain = str(event.domain);
      if (domain) detail.domain = domain;
      if (typeof event.chars === 'number') detail.chars = event.chars;
      onStage?.(detail);
    }
  };

  try {
    for (;;) {
      const { value, done } = await reader.read();
      if (done) break;
      buffer += decoder.decode(value, { stream: true });

      // SSE frames are separated by a blank line. Anything after the last
      // separator is an incomplete frame and stays in the buffer.
      let sep = buffer.search(/\r?\n\r?\n/);
      while (sep !== -1) {
        const frame = buffer.slice(0, sep);
        buffer = buffer.slice(sep + buffer.slice(sep).match(/^\r?\n\r?\n/)![0].length);
        handleFrame(frame);
        sep = buffer.search(/\r?\n\r?\n/);
      }
    }
    buffer += decoder.decode();
    if (buffer.trim()) handleFrame(buffer); // Server closed without a trailing blank line.
  } finally {
    reader.cancel().catch(() => undefined);
  }

  if (!result) {
    throw new BriefblipError(0, 'The stream ended before the brief was ready. Please try again.');
  }
  return result;
}

// ---------------------------------------------------------------------------
// Rung 3 — weekly brief signup
// ---------------------------------------------------------------------------

export interface SubscribeResult {
  ok: boolean;
  status: string;
  /** Present only when status is "checkout" — a real purchase action, see below. */
  checkoutUrl?: string;
  /** Present only when status is "already_pro" — where to manage an existing subscription. */
  manageUrl?: string;
}

/**
 * 🔴 For tier "free", the backend answers { ok, status: "pending" } for a brand-new
 * address, a pending one asking again, and one that is already subscribed — deliberately
 * indistinguishable, so the endpoint cannot be used to enumerate who subscribes to a
 * regulatory mailing list. The UI must not undo that: never branch the free-path message on
 * the response, and never say "you are already subscribed".
 *
 * tier "pro" is a genuinely different action — an explicit purchase request, not a passive
 * membership check — so its response is NOT covered by that guarantee. It answers with a
 * status of "checkout" (redirect the browser to checkoutUrl) or "already_pro" (redirect to
 * manageUrl instead of minting a second Stripe subscription).
 */
export async function subscribeWeekly(
  email: string,
  tier: 'free' | 'pro' = 'free',
  signal?: AbortSignal,
): Promise<SubscribeResult> {
  const res = await fetch(`${API_BASE}/api/briefblip/subscribe.php`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
    body: JSON.stringify({ email, tier }),
    signal,
  });

  if (!res.ok) throw await errorFromResponse(res);

  const body = (await res.json()) as {
    ok?: unknown;
    status?: unknown;
    checkout_url?: unknown;
    manage_url?: unknown;
  };
  return {
    ok: body.ok === true,
    status: str(body.status) || 'pending',
    checkoutUrl: str(body.checkout_url) || undefined,
    manageUrl: str(body.manage_url) || undefined,
  };
}

// ---------------------------------------------------------------------------

export function briefToMarkdown(result: BlipResult, sourceUrl?: string | null): string {
  const { brief } = result;
  const lines: string[] = [`# ${brief.title}`, ''];

  if (sourceUrl) lines.push(`Source: <${sourceUrl}>`, '');
  if (brief.tl_dr) lines.push(brief.tl_dr, '');

  lines.push('## Key points', '');
  if (brief.key_points.length) {
    for (const kp of brief.key_points) {
      lines.push(`- ${kp.point}`);
      if (kp.quote) lines.push(`  > ${kp.quote}`);
    }
  } else {
    lines.push('_None recorded._');
  }
  lines.push('');

  if (brief.facts_figures.length) {
    lines.push('## Facts & figures', '');
    brief.facts_figures.forEach((f) => lines.push(`- ${f}`));
    lines.push('');
  }

  if (brief.who_should_care) {
    lines.push('## Who should care', '', brief.who_should_care, '');
  }

  if (result.url) lines.push(`Permalink: <${result.url}>`, '');

  lines.push('---');
  lines.push('_Briefed by BriefBlip — a Blue Note Logic Inc. product._');
  return lines.join('\n');
}
